import type { LucideIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { buttonClass, iconButtonClass, TipBubble, type ButtonSize, type ButtonVariant } from './Button'
import { cx } from './cx'

export type MenuEntry =
  | {
      label: string
      icon?: LucideIcon
      onSelect: () => void
      /** Destructive action: red text. Still confirm if irreversible. */
      danger?: boolean
      disabled?: boolean
      /** Right-aligned hint, e.g. a shortcut. */
      hint?: string
    }
  | 'separator'

type MenuProps = {
  /** Trigger name. Visible text unless `icon` is given without `showLabel`. */
  label: string
  icon?: LucideIcon
  showLabel?: boolean
  variant?: Exclude<ButtonVariant, 'danger'>
  size?: ButtonSize
  items: MenuEntry[]
  /** Which trigger edge the menu aligns to. */
  align?: 'start' | 'end'
}

const supportsAnchor = typeof CSS !== 'undefined' && CSS.supports('anchor-name: --a')

/** Fallback placement where CSS anchor positioning is missing: below (or above) the trigger, kept on screen. */
function place(menu: HTMLElement, trigger: HTMLElement | null, align: 'start' | 'end') {
  const t = trigger?.getBoundingClientRect()
  if (!t) return
  const m = menu.getBoundingClientRect()
  const below = t.bottom + 4 + m.height <= innerHeight || t.top < m.height
  const left = align === 'end' ? t.right - m.width : t.left
  menu.style.top = `${below ? t.bottom + 4 : t.top - m.height - 4}px`
  menu.style.left = `${Math.max(8, Math.min(left, innerWidth - m.width - 8))}px`
}

/**
 * Action menu on the Popover API: top layer, light dismiss and Esc are native; focus returns to the
 * trigger. CSS anchor positioning places it; a few lines of JS do it where that is unsupported.
 */
export function Menu({ label, icon: Icon, showLabel = !Icon, variant = 'ghost', size = 'md', items, align = 'end' }: MenuProps) {
  const id = useId()
  const anchor = `--menu-${id.replace(/[^a-zA-Z0-9]/g, '')}`
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const menu = menuRef.current
    if (!menu) return
    const onToggle = (e: Event) => {
      const isOpen = (e as ToggleEvent).newState === 'open'
      setOpen(isOpen)
      if (!isOpen) return
      if (!supportsAnchor) place(menu, triggerRef.current, align)
      menu.querySelector<HTMLButtonElement>('[role=menuitem]:not(:disabled)')?.focus()
    }
    menu.addEventListener('toggle', onToggle)
    return () => menu.removeEventListener('toggle', onToggle)
  }, [align])

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const all = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not(:disabled)') ?? [])]
    const i = all.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      e.key === 'ArrowDown' ? (i + 1) % all.length
      : e.key === 'ArrowUp' ? (i - 1 + all.length) % all.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? all.length - 1
      : -1
    if (next >= 0) {
      e.preventDefault()
      all[next]?.focus()
    } else if (e.key === 'Tab') {
      menuRef.current?.hidePopover()
    } else if (e.key.length === 1 && /\S/.test(e.key)) {
      // Type-ahead: jump to the next item starting with the typed letter.
      const k = e.key.toLowerCase()
      const hit = [...all.slice(i + 1), ...all.slice(0, i + 1)].find((b) => b.textContent?.trim().toLowerCase().startsWith(k))
      hit?.focus()
    }
  }

  const iconOnly = Icon && !showLabel
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        popoverTarget={`${id}-menu`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        aria-label={iconOnly ? label : undefined}
        // Dynamic anchor name per instance; the only way to tie CSS anchor positioning to this trigger.
        style={{ anchorName: anchor }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            const menu = menuRef.current
            if (menu && !menu.matches(':popover-open')) menu.showPopover()
          }
        }}
        className={cx(
          iconOnly ? iconButtonClass({ variant, size }) : buttonClass({ variant, size }),
          'aria-expanded:bg-bg-hover aria-expanded:text-ink',
        )}
      >
        {Icon && <Icon aria-hidden="true" strokeWidth={iconOnly ? 2.25 : 2} />}
        {showLabel && label}
        {iconOnly && <TipBubble>{label}</TipBubble>}
      </button>
      <div
        ref={menuRef}
        id={`${id}-menu`}
        popover="auto"
        role="menu"
        tabIndex={-1}
        aria-label={label}
        onKeyDown={onKeyDown}
        style={{ positionAnchor: anchor }}
        className={cx(
          'inset-auto m-0 min-w-52 max-w-72 animate-pop rounded-card border border-line bg-surface p-1 text-ink shadow-float',
          supportsAnchor &&
            (align === 'end'
              ? '[position-area:bottom_span-left] menu-fallbacks'
              : '[position-area:bottom_span-right] menu-fallbacks'),
          supportsAnchor && 'mt-1',
          !supportsAnchor && 'fixed',
        )}
      >
        {items.map((item, i) =>
          item === 'separator' ? (
            <hr key={i} className="-mx-1 my-1 border-line" />
          ) : (
            <button
              key={i}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              tabIndex={-1}
              onClick={() => {
                menuRef.current?.hidePopover()
                item.onSelect()
              }}
              className={cx(
                'flex h-11 w-full items-center gap-2.5 rounded-[6px] px-2.5 text-left text-body outline-none pointer-fine:h-9 pointer-fine:text-small',
                'hover:bg-bg-hover focus-visible:bg-bg-hover focus:bg-bg-hover disabled:pointer-events-none disabled:opacity-45',
                item.danger ? 'text-negative' : 'text-ink',
              )}
            >
              {item.icon && (
                <item.icon aria-hidden="true" className={cx('size-4 shrink-0', item.danger ? 'text-negative' : 'text-ink-3')} />
              )}
              <span className="flex-1 truncate">{item.label}</span>
              {item.hint && <span className="text-label text-ink-3">{item.hint}</span>}
            </button>
          ),
        )}
      </div>
    </>
  )
}
